/* import __COLOCATED_TEMPLATE__ from './pane.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { useResource } from 'ember-resources';
import type IntlService from 'embercom/services/intl';
import NavigableSelection from 'embercom/components/inbox2/common/navigable-selection-resource';
import EmojiNavigationResource from 'embercom/components/inbox2/command-k/emoji-navigation-resource';
import type EmojiAction from 'embercom/objects/inbox/command-k/emoji';
import { ChildAction } from 'embercom/objects/inbox/command-k/action';

// @ts-ignore
import { ref } from 'ember-ref-bucket';

// @ts-ignore
import { cached } from 'tracked-toolbox';

// @ts-ignore
import intermoji from '@intercom/intermoji';
import storage from 'embercom/vendor/intercom/storage';
import type CommandKService from 'embercom/services/command-k';
import type Session from 'embercom/services/session';
import {
  type DefaultYellowSkinTone,
  type SkinToneModifiers,
} from 'embercom/services/emoji-service';
import type EmojiService from 'embercom/services/emoji-service';
import type CustomEmojiService from 'embercom/services/custom-emoji-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';
import { type CommandKPaneComponentArgs } from 'embercom/lib/inbox2/command-k-types';

interface Signature {
  Args: CommandKPaneComponentArgs<EmojiAction>;
}

const EMOJI_PER_PAGE = 100;
const DEFAULT_EMOJIS = Object.freeze([
  'thumbs_up',
  '-1',
  'sob',
  'confused',
  'neutral_face',
  'blush',
  'heart_eyes',
]);
let FREQUENTLY_USED_STORAGE_KEY = 'frequentlyUsedEmojis';

interface EmojiUsage {
  emoji: string;
  count: number;
}

type Icon = string;
export type ItemGroups = Record<string, ChildAction[]>;

export default class EmojiPaneComponent extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare commandK: CommandKService;
  @service declare session: Session;
  @service declare emojiService: EmojiService;
  @service declare customEmojiService: CustomEmojiService;

  @ref('ul') declare ul: HTMLUListElement;

  @tracked query = '';
  @tracked limit = EMOJI_PER_PAGE;

  readonly selection = useResource(this, NavigableSelection, () => ({
    items: this.items,
    isCommandKVisible: this.commandK.isVisible,
  }));

  readonly keyboardNavigableItemsResource = useResource(this, EmojiNavigationResource, () => ({
    selection: this.selection,
    items: this.itemGroups,
  }));

  get skinToneModifier() {
    return this.emojiService.skinToneModifier;
  }

  get allEmojisVisible() {
    return this.allEmojis.length <= this.items.length;
  }

  get items() {
    return Object.values(this.itemGroups).flat();
  }

  get itemGroups() {
    // Get frequently used and custom emojis
    let frequentlyUsedEmojis = this.getFrequentlyUsedEmojis();
    let customEmojiActions: ChildAction[] = [];
    if (this.session.workspace.isFeatureEnabled('team-messenger-custom-emojis')) {
      customEmojiActions = this.getCustomEmojiActions();
    }

    let regexp = new RegExp(this.query, 'i');
    let currentIndex = 0;

    // Force ordering by explicitly building the object in the desired order
    let orderedGroups: Record<string, ChildAction[]> = {};

    // 1. Add frequently used emojis FIRST (highest priority)
    if (!this.query && frequentlyUsedEmojis.length > 0) {
      let frequentlyUsedKey = this.intl.t('inbox.command-k.emojis.frequently-used');
      orderedGroups[frequentlyUsedKey] = frequentlyUsedEmojis;
      currentIndex += frequentlyUsedEmojis.length;
    }

    // 2. Add custom emojis SECOND
    if (!this.query && customEmojiActions.length > 0) {
      orderedGroups[this.intl.t('inbox.command-k.emojis.custom-emojis')] = customEmojiActions;
      currentIndex += customEmojiActions.length;
    }

    // Handle search filtering for custom emojis
    if (this.query && customEmojiActions.length > 0) {
      let matchingCustomEmojis = customEmojiActions.filter((action: ChildAction) =>
        regexp.test(action.label),
      );
      if (matchingCustomEmojis.length > 0) {
        orderedGroups[this.intl.t('inbox.command-k.emojis.custom-emojis')] = matchingCustomEmojis;
      }
    }

    // 3. Add regular emoji groups THIRD
    for (let [index, unicodeIcons] of this.emojiGroups.entries()) {
      let [, groupName] = this.groupRepresentatives[index];

      if (!orderedGroups[groupName]) {
        orderedGroups[groupName] = [];
      }

      for (let icon of unicodeIcons) {
        if (currentIndex >= this.limit) {
          break;
        }

        let identifier = intermoji.identifierFromUnicode(icon);
        if (intermoji.isEmojiBaseUnicode(icon) && regexp.test(identifier)) {
          currentIndex += 1;
          orderedGroups[groupName].push(
            this.actionFor(identifier, identifier, this.emojiService.skinTonedModifiedEmoji(icon)),
          );
        }
      }

      if (currentIndex >= this.limit) {
        break;
      }
    }
    return orderedGroups;
  }

  @action loadMore() {
    if (this.limit >= this.allEmojis.length) {
      return;
    }

    let oldLimit = this.limit;
    this.limit += EMOJI_PER_PAGE;
    this.selection.select(this.items[Math.min(oldLimit, this.items.length - 1)]);
  }

  @action search(query: string) {
    this.query = query;
    this.commandK.trackSearch({
      query: this.query,
      number_of_results: this.items.length,
    });
  }

  @action updateSkinToneModifier(skinToneModifier: SkinToneModifiers | DefaultYellowSkinTone) {
    this.emojiService.updateSkinToneModifier(skinToneModifier);
  }

  @cached
  private get allEmojis() {
    if (
      this.skinToneModifier !== ('default-yellow-skin-tone' as DefaultYellowSkinTone) &&
      this.skinToneModifier !== null
    ) {
      return this.allEmojisBySkinToneModifier.get(this.skinToneModifier);
    }

    return this.allBaseEmojis;
  }

  @cached
  private get allBaseEmojis() {
    return this.emojiGroups.flat().filter((icon: Icon) => intermoji.isEmojiBaseUnicode(icon));
  }

  @cached
  private get allEmojisBySkinToneModifier() {
    let allBaseEmojis = this.allBaseEmojis;

    let emojisBySkinToneModifier = new Map<number, Array<Icon>>();
    let skinToneModifiers = ['🏻', '🏼', '🏽', '🏾', '🏿'].map(
      (skinTone) => skinTone.codePointAt(0) as number,
    );
    let modifiedEmojiBySkinToneModifierMemo = new Map<number, Icon | null>();

    skinToneModifiers.forEach((skinToneModifier) => {
      emojisBySkinToneModifier.set(skinToneModifier, []);
      modifiedEmojiBySkinToneModifierMemo.set(skinToneModifier, null);
    });

    for (let emoji of allBaseEmojis) {
      let modifiedEmojis = intermoji.getAllModifiedForBaseEmoji(emoji) as Array<Icon>;

      if (modifiedEmojis.length > 1) {
        for (let skinToneModifierKey of skinToneModifiers.keys()) {
          modifiedEmojiBySkinToneModifierMemo.set(skinToneModifierKey, null);
        }

        for (let modifiedEmoji of modifiedEmojis) {
          let codePoints = Array.from(modifiedEmoji).map((char) => char.codePointAt(0) as number);

          for (let codePoint of codePoints) {
            if (modifiedEmojiBySkinToneModifierMemo.has(codePoint)) {
              modifiedEmojiBySkinToneModifierMemo.set(codePoint, modifiedEmoji);
            }
          }
        }
      }

      for (let skinToneModifier of skinToneModifiers) {
        let emojis = emojisBySkinToneModifier.get(skinToneModifier) as Array<Icon>;

        if (modifiedEmojis.length === 1) {
          emojis.push(modifiedEmojis[0]);
        } else {
          let matchedModifiedEmoji = modifiedEmojiBySkinToneModifierMemo.get(skinToneModifier);
          emojis.push(matchedModifiedEmoji || emoji);
        }

        emojisBySkinToneModifier.set(skinToneModifier, emojis);
      }
    }

    return emojisBySkinToneModifier;
  }

  @cached
  private get emojiGroups() {
    return intermoji.prettyEmoticonsUnicodeGroups();
  }

  @cached
  private get groupRepresentatives() {
    return intermoji.getGroupRepresentatives();
  }

  private getFrequentlyUsedEmojis(): ChildAction[] {
    let userEmojis = this.getUserEmojis();

    return userEmojis
      .map((emojiOrIdentifier: string) => {
        let resolved = this.resolveEmojiAndIdentifier(emojiOrIdentifier);
        if (!resolved) {
          return;
        }

        let { icon, identifier, customEmoji } = resolved;

        // If it's a custom emoji, create a custom emoji action
        if (customEmoji) {
          return this.actionForCustomEmoji(customEmoji);
        }

        // Apply skin tone modification if user has selected one
        // The skinTonedModifiedEmoji method will return the original emoji if:
        // - No skin tone modifier is set
        // - The emoji doesn't support skin tones
        // We need to use the original icon (not baseEmojiFor) to preserve variation selectors
        let displayEmoji = this.emojiService.skinTonedModifiedEmoji(icon);

        return this.actionFor(`frequent-${identifier}`, identifier, displayEmoji);
      })
      .compact();
  }

  /**
   * Gets user's frequently used emojis from storage.
   * If no storage exists, initializes with default emojis (count: 1).
   * Returns array of emoji identifiers sorted by frequency.
   */
  private getUserEmojis(): string[] {
    let stored = storage.get(FREQUENTLY_USED_STORAGE_KEY);

    // Storage exists - extract emoji identifiers (already sorted by frequency)
    if (stored && Array.isArray(stored) && stored.length > 0) {
      // Validate and filter data to ensure it matches EmojiUsage format
      return stored
        .filter(
          (item): item is EmojiUsage =>
            typeof item === 'object' &&
            item !== null &&
            'emoji' in item &&
            'count' in item &&
            typeof item.emoji === 'string' &&
            typeof item.count === 'number',
        )
        .map((item) => item.emoji);
    }

    // No storage exists - initialize with default emojis
    let defaultEmojiData = DEFAULT_EMOJIS.map((emoji) => ({
      emoji,
      count: 1,
    }));

    storage.set(FREQUENTLY_USED_STORAGE_KEY, defaultEmojiData);

    return [...DEFAULT_EMOJIS];
  }

  /**
   * Resolves an emoji or identifier string to its icon and identifier representation.
   * Handles old format (identifiers), new format (unicode emojis), and custom emojis.
   *
   * @param emojiOrIdentifier - Either a unicode emoji, emoji identifier, or custom emoji identifier
   * @returns Object with icon, identifier, and optional customEmoji, or undefined if the input is invalid
   */
  private resolveEmojiAndIdentifier(
    emojiOrIdentifier: string,
  ): { icon: string; identifier: string; customEmoji?: CustomEmoji } | undefined {
    // Try to convert from identifier (old format) first
    let convertedIcon = intermoji.unicodeFromIdentifier(emojiOrIdentifier);
    if (convertedIcon) {
      return { icon: convertedIcon, identifier: emojiOrIdentifier };
    }

    // Check if it's a custom emoji
    if (this.session.workspace.isFeatureEnabled('team-messenger-custom-emojis')) {
      let customEmoji = this.customEmojiService.getCustomEmoji(emojiOrIdentifier);
      if (customEmoji) {
        return {
          icon: emojiOrIdentifier,
          identifier: emojiOrIdentifier,
          customEmoji,
        };
      }
    }

    // Not an identifier, treat as unicode emoji
    if (!intermoji.isSupportedUnicode(emojiOrIdentifier)) {
      // Unsupported emoji (newer emojis not in intermoji database like 🥾)
      return { icon: emojiOrIdentifier, identifier: emojiOrIdentifier };
    }

    // Supported unicode emoji - only return if it's a base emoji (skip skin tone variants)
    if (!intermoji.isEmojiBaseUnicode(emojiOrIdentifier)) {
      return;
    }

    return {
      icon: emojiOrIdentifier,
      identifier: intermoji.identifierFromUnicode(emojiOrIdentifier) || emojiOrIdentifier,
    };
  }

  private getCustomEmojiActions(): ChildAction[] {
    return this.customEmojiService.getAllCustomEmojis().map((customEmoji) => {
      return this.actionForCustomEmoji(customEmoji);
    });
  }

  private actionForCustomEmoji(customEmoji: CustomEmoji): ChildAction {
    let action = new ChildAction({
      parent: this.args.actionItem,
      id: `custom-${customEmoji.identifier}`,
      label: customEmoji.identifier,
      value: customEmoji, // Pass the custom emoji object
    });
    return action;
  }

  private actionFor(id: string, identifier: string, icon: Icon) {
    return new ChildAction({
      parent: this.args.actionItem,
      id,
      label: identifier,
      value: icon,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::CommandK::Emoji::Pane': typeof EmojiPaneComponent;
    'inbox2/command-k/emoji/pane': typeof EmojiPaneComponent;
  }
}
